import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import MainNavbar from '../../components/Navbar/MainNavbar'
import Typography from '../../components/Typography'
import Button from '../../components/Button/Button'
import { useAuth } from '../../context/AuthContext'
import useSessionLocation, { LOCATION_ERROR_CODES } from '../../hooks/useSessionLocation'
import usePeopleDiscovery, { LIST_STATUS } from '../../hooks/usePeopleDiscovery'
import PeopleTabs from './components/PeopleTabs'
import PeopleSearchBar from './components/PeopleSearchBar'
import PeopleList from './components/PeopleList'
import PeopleStateView from './components/PeopleStateView'

const TAB_COMMUNITY = 'community'
const TAB_NEARBY = 'nearby'
const PAGE_LIMIT = 20

const getLocationErrorMessageKey = (errorCode) => {
  if (errorCode === LOCATION_ERROR_CODES.PERMISSION_DENIED) return 'nearby.locationErrorPermission'
  if (errorCode === LOCATION_ERROR_CODES.POSITION_UNAVAILABLE) return 'nearby.locationErrorPositionUnavailable'
  if (errorCode === LOCATION_ERROR_CODES.TIMEOUT) return 'nearby.locationErrorTimeout'
  if (errorCode === LOCATION_ERROR_CODES.UNSUPPORTED) return 'nearby.locationErrorUnsupported'
  return 'nearby.locationError'
}

const PeopleDiscovery = () => {
  const { t } = useTranslation('peopleDiscovery')
  const navigate = useNavigate()
  const { follow, unfollow, followingSet, followingReady } = useAuth()

  const [activeTab, setActiveTab] = useState(TAB_COMMUNITY)
  const [pendingFollowIds, setPendingFollowIds] = useState(new Set())

  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')

  const {
    sessionCoords,
    locationErrorCode,
    requestLocation
  } = useSessionLocation()

  const {
    community,
    nearby,
    search,
    requiresLocationSetup,
    communityUsers,
    nearbyUsers,
    searchUsers,
    loadCommunity,
    loadNearby,
    loadSearchGlobal,
    resetSearch,
    patchUserFollowState
  } = usePeopleDiscovery({ pageLimit: PAGE_LIMIT })

  const initialSessionCoordsRef = useRef(sessionCoords)

  useEffect(() => {
    loadCommunity({ page: 1, replace: true })
    loadNearby({ page: 1, replace: true, coords: initialSessionCoordsRef.current })
  }, [loadCommunity, loadNearby])

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(String(searchQuery || '').trim())
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery])

  useEffect(() => {
    if (debouncedQuery.length < 2) {
      resetSearch()
      return
    }

    loadSearchGlobal({ q: debouncedQuery, page: 1, replace: true })
  }, [debouncedQuery, loadSearchGlobal, resetSearch])

  const isGlobalSearchActive = debouncedQuery.length >= 2
  const shouldShowMinCharsHint = searchQuery.trim().length > 0 && !isGlobalSearchActive

  const applyFollowingSet = useCallback((users = []) => {
    if (!followingReady || !followingSet) return users
    return users.map(item => ({
      ...item,
      isFollowing: Boolean(item.isFollowing || followingSet.has(String(item.id)))
    }))
  }, [followingReady, followingSet])

  const renderedCommunityUsers = useMemo(() => applyFollowingSet(communityUsers), [communityUsers, applyFollowingSet])
  const renderedNearbyUsers = useMemo(() => applyFollowingSet(nearbyUsers), [nearbyUsers, applyFollowingSet])
  const renderedSearchUsers = useMemo(() => applyFollowingSet(searchUsers), [searchUsers, applyFollowingSet])

  const handleOpenProfile = useCallback((person) => {
    navigate(`/user/${person.id}`)
  }, [navigate])

  const handleToggleFollow = async (person) => {
    const targetId = String(person.id)
    if (!targetId) return

    const currentlyFollowing = Boolean(person.isFollowing || (followingReady && followingSet && followingSet.has(targetId)))

    setPendingFollowIds(prev => {
      const next = new Set(prev)
      next.add(targetId)
      return next
    })

    try {
      if (currentlyFollowing) {
        await unfollow(targetId)
        patchUserFollowState(targetId, false)
      } else {
        await follow(targetId)
        patchUserFollowState(targetId, true)
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('errors.followAction'))
    } finally {
      setPendingFollowIds(prev => {
        const next = new Set(prev)
        next.delete(targetId)
        return next
      })
    }
  }

  const handleGetSessionLocation = async () => {
    const locationResult = await requestLocation()

    if (!locationResult.ok || !locationResult.coords) {
      const messageKey = getLocationErrorMessageKey(locationResult.errorCode || locationErrorCode)
      toast.error(t(messageKey))
      return
    }

    await loadNearby({ page: 1, replace: true, coords: locationResult.coords })
    toast.success(t('nearby.locationUpdated'))
  }

  return (
    <>
      <MainNavbar />
      <PageWrapper>
        <Container>
          <Header>
            <Typography as="h1" $variant="title-4-semibold" $variantTablet="title-5-semibold" $variantMobile="body-1-semibold">
              {t('title')}
            </Typography>
          </Header>

          <PeopleTabs
            activeTab={activeTab}
            onChange={setActiveTab}
            tabs={[
              { id: TAB_COMMUNITY, label: t('tabs.community') },
              { id: TAB_NEARBY, label: t('tabs.nearby') }
            ]}
          />

          <PeopleSearchBar
            value={searchQuery}
            placeholder={t('search.placeholder')}
            clearLabel={t('search.clear')}
            onChange={setSearchQuery}
            onClear={() => setSearchQuery('')}
          />

          {!isGlobalSearchActive && activeTab === TAB_NEARBY && (
            <InlineActions>
              <Button $variant="ghost" onClick={handleGetSessionLocation} disabled={nearby.status === LIST_STATUS.LOADING}>
                {t('nearby.getLocation')}
              </Button>
            </InlineActions>
          )}

          {shouldShowMinCharsHint && (
            <PeopleStateView title={t('search.minChars')} />
          )}

          {isGlobalSearchActive && search.status === LIST_STATUS.LOADING && (
            <PeopleStateView title={t('states.searchingGlobal')} />
          )}

          {isGlobalSearchActive && search.status === LIST_STATUS.ERROR && (
            <PeopleStateView title={t('states.error')} />
          )}

          {isGlobalSearchActive && search.status !== LIST_STATUS.LOADING && search.status !== LIST_STATUS.ERROR && search.loadedOnce && renderedSearchUsers.length === 0 && (
            <PeopleStateView title={t('search.empty')} />
          )}

          {isGlobalSearchActive && renderedSearchUsers.length > 0 && (
            <>
              <PeopleList
                users={renderedSearchUsers}
                showFollowersCount
                followLabel={t('actions.follow')}
                followingLabel={t('actions.following')}
                followersCountLabel={(count) => t('community.followersCount', { count })}
                pendingFollowIds={pendingFollowIds}
                onOpenProfile={handleOpenProfile}
                onToggleFollow={handleToggleFollow}
              />
              {search.hasMore && (
                <Actions>
                  <Button
                    $variant="ghost"
                    onClick={() => loadSearchGlobal({ q: debouncedQuery, page: search.page + 1, replace: false })}
                    disabled={search.status === LIST_STATUS.LOADING}
                  >
                    {t('actions.loadMore')}
                  </Button>
                </Actions>
              )}
            </>
          )}

          {!isGlobalSearchActive && activeTab === TAB_COMMUNITY && community.status === LIST_STATUS.LOADING && !community.loadedOnce && (
            <PeopleStateView title={t('states.loading')} />
          )}

          {!isGlobalSearchActive && activeTab === TAB_COMMUNITY && community.status === LIST_STATUS.ERROR && (
            <PeopleStateView title={t('states.error')} />
          )}

          {!isGlobalSearchActive && activeTab === TAB_COMMUNITY && community.status !== LIST_STATUS.LOADING && community.status !== LIST_STATUS.ERROR && community.loadedOnce && renderedCommunityUsers.length === 0 && (
            <PeopleStateView title={t('community.empty')} />
          )}

          {!isGlobalSearchActive && activeTab === TAB_COMMUNITY && renderedCommunityUsers.length > 0 && (
            <>
              <PeopleList
                users={renderedCommunityUsers}
                showFollowersCount
                followLabel={t('actions.follow')}
                followingLabel={t('actions.following')}
                followersCountLabel={(count) => t('community.followersCount', { count })}
                pendingFollowIds={pendingFollowIds}
                onOpenProfile={handleOpenProfile}
                onToggleFollow={handleToggleFollow}
              />
              {community.hasMore && (
                <Actions>
                  <Button
                    $variant="ghost"
                    onClick={() => loadCommunity({ page: community.page + 1, replace: false })}
                    disabled={community.status === LIST_STATUS.LOADING}
                  >
                    {t('actions.loadMore')}
                  </Button>
                </Actions>
              )}
            </>
          )}

          {!isGlobalSearchActive && activeTab === TAB_NEARBY && nearby.status === LIST_STATUS.LOADING && !nearby.loadedOnce && (
            <PeopleStateView title={t('states.loading')} />
          )}

          {!isGlobalSearchActive && activeTab === TAB_NEARBY && nearby.status === LIST_STATUS.ERROR && (
            <PeopleStateView title={t('states.error')} />
          )}

          {!isGlobalSearchActive && activeTab === TAB_NEARBY && nearby.status !== LIST_STATUS.ERROR && nearby.status !== LIST_STATUS.LOADING && requiresLocationSetup && (
            <PeopleStateView
              title={t('nearby.locationRequired')}
              description={t('nearby.locationRequiredDetail')}
              actionLabel={t('nearby.getLocation')}
              onAction={handleGetSessionLocation}
            />
          )}

          {!isGlobalSearchActive && activeTab === TAB_NEARBY && nearby.status !== LIST_STATUS.LOADING && nearby.status !== LIST_STATUS.ERROR && !requiresLocationSetup && nearby.loadedOnce && renderedNearbyUsers.length === 0 && (
            <PeopleStateView title={t('nearby.empty')} />
          )}

          {!isGlobalSearchActive && activeTab === TAB_NEARBY && renderedNearbyUsers.length > 0 && (
            <>
              <PeopleList
                users={renderedNearbyUsers}
                followLabel={t('actions.follow')}
                followingLabel={t('actions.following')}
                followersCountLabel={(count) => t('community.followersCount', { count })}
                pendingFollowIds={pendingFollowIds}
                onOpenProfile={handleOpenProfile}
                onToggleFollow={handleToggleFollow}
              />
              {nearby.hasMore && (
                <Actions>
                  <Button
                    $variant="ghost"
                    onClick={() => loadNearby({ page: nearby.page + 1, replace: false, coords: sessionCoords })}
                    disabled={nearby.status === LIST_STATUS.LOADING}
                  >
                    {t('actions.loadMore')}
                  </Button>
                </Actions>
              )}
            </>
          )}
        </Container>
      </PageWrapper>
    </>
  )
}

export default PeopleDiscovery

const PageWrapper = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => theme.sizing.sm} ${({ theme }) => theme.sizing.md};
  width: 100%;
  max-width: 1400px;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.sm};
  }
`

const Header = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`

const InlineActions = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
`

const Actions = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`
